/*
 * Copyright 2024 The Kokabo Authors. All rights reserved.
 * Use of this source code is governed by a BSD-style
 * license that can be found in the LICENSE file.
 */


import {Log} from "../utils/Log.js";
import {Utils} from "../utils/Utils.js";
import {TimeoutForOnce, UnLimited, Once, CallInstruction, AnswerInstruction, ErrorInstruction} from "./consts.js"
import {SecurityAgent} from "../e2ee/SecurityAgent.js";
import {Addresses, DefaultAddresses} from "./Addresses.js";
import {RemoteApi} from "./RemoteApi.js";
import {HubInfo} from "./HubInfo.js";
import {Assembler} from "./Assembler.js";
import {ExecutionMessage, Fragment, NoEncoding} from "./ExecutionMessage.js";
import {ActorEvent} from "./ActorEvent.js";
import {ActorEventListener} from "./ActorEventListener.js";
import {EmitterReplyListener} from "./EmitterReplyListener.js";
import {ActorError} from "./ActorError.js";


/**
 * Transport-agnostic endpoint for the Kokabo RPC/event protocol.
 *
 * Subclasses implement the concrete transport (WebSocket, SSE+REST). This base class owns
 * protocol semantics: call ids, routing, fragmentation reassembly, and reply dispatch.
 *
 * @extends EventTarget
 */
class Actor extends EventTarget {

    /**
     * @typedef {Object} Reservation
     * @property {string} ticket - Opaque reservation token (JSON) encrypted for the hub; binds a claimed name to a connection attempt.
     */


    /**
     * Connection metadata for the target hub (name/code/host + negotiated AES key).
     * @var {HubInfo}
     */
    hubInfo;


    /**
     * Convenience wrapper around hub REST endpoints for actor lifecycle management.
     * @var {RemoteApi}
     */
    remoteApi;

    /**
     * Optional claimed identity used by the hub to provide a stable, human-meaningful endpoint.
     * @var {String}
     */
    name;

    /**
     * Optional secret required to claim a name, mitigating name squatting.
     * @var {String}
     */
    activation;

    connected = false;
    explicitlyDisconnected = false;
    sseMode = false;


    _incrementalId = 0;

    // Reconnect with exponential backoff to avoid tight retry loops and reduce hub load during outages.
    _reconnectAttempts = 0;
    _reconnectDelay = 1_000; // ms; backoff base.
    _maxReconnectDelay = 60_000; // ms; cap to keep retries responsive.
    _maxReconnectAttempts = UnLimited; // Sentinel: keep retrying until explicitly disconnected.

    /**
     * Cached ticket from the last successful name reservation.
     * @type {string}
     * @private
     */
    _lastTicket = ""

    /**
     * Local listener registry keyed by an event type.
     * Kept internal (rather than relying on native EventTarget dispatch) to keep delivery semantics consistent across runtimes.
     * @type {Map<string, Array<ActorEventListener>>}
     */
    listeners = new Map();

    /**
     * In-flight calls awaiting replies, keyed by callId.
     * @type {Map<string, EmitterReplyListener>}
     */
    emitters = new Map();


    /**
     * @param {HubInfo} hubInfo
     * @param {string} actorName
     * @param {string} actorActivation
     * @constructor
     */
    constructor(hubInfo, actorName, actorActivation) {
        super();
        this.UUID = Utils.v4UUID();
        this.calls = {};
        this.name = actorName;
        this.activation = actorActivation;
        this.hubInfo = hubInfo;
        this.remoteApi = new RemoteApi(this.UUID, this.hubInfo);
        this.assemblers = new Map(); // Fragmentation is scoped per callId; keep one assembler per in-flight reassembly.
    }


    /**
     * Monotonic per-actor call id used by the protocol and (in SSE mode) for last-event resume.
     * Hex encoding keeps identifiers compact while remaining human-debuggable.
     * @returns {string}
     */
    hexIncrementalID() {
        this._incrementalId++;
        return this._incrementalId.toString(16);
    }


    /**
     * Public connect entrypoint; subclasses provide the transport via {@link Actor#connectTo}.
     * @param {string} hubUrl
     * @returns {Promise<boolean|error>}
     */
    async connect(hubUrl) {
        return this.connectionAttempt(hubUrl, false);
    }

    /**
     * Centralizes connection + (optional) retry strategy so transports stay thin.
     * @param {String} hubUrl
     * @param reconnection if true it will apply the reconnection strategy
     * @return {Promise<boolean|error>}
     */
    async connectionAttempt(hubUrl, reconnection = false) {
        this.hubUrl = hubUrl;
        if (reconnection) {
            if (this._reconnectAttempts >= this._maxReconnectAttempts) {
                Log.error('[Actor._connectionAttempt] Max reconnection attempts reached');
                return Promise.reject(new Error('Max reconnection attempts reached'));
            }
            // Backoff is exponential and capped to avoid overwhelming the hub or burning client CPU during extended outages.
            let delay = Math.min(this._reconnectDelay * Math.pow(2, this._reconnectAttempts), this._maxReconnectDelay);
            await new Promise(resolve => setTimeout(resolve, delay));
            this._reconnectAttempts++;
        }
        try {
            if (this.name !== undefined && this.name !== "") {
                // Reserve the claimed name up front so the transport URL carries a hub-issued ticket (prevents late rejection).
                try {
                    const reservation = await this.claimName(this.name, this.activation);
                    if (reservation && reservation.ticket) {
                        this._lastTicket = Utils.hexEncodeToString(reservation.ticket);
                        hubUrl += `?ticket=${this._lastTicket}&activation=${this.activation}`;
                    }
                    if (this.sseMode && this._incrementalId > 0) {
                        hubUrl += `&lastEventId=${this._incrementalId}`;
                    }
                } catch (e) {
                    return Promise.reject(e);
                }
            }
            Log.info('[Actor.connect] Connecting', this.name ? `as ${this.name}` : '', 'to', hubUrl);
            return await this.connectTo(hubUrl);
        } catch (e) {
            Log.error('[Actor.connect] Error registering secure UUID:', e.message);
            return Promise.reject(e);
        }
    }

    /**
     * Transport hook (must be overridden).
     * @param {string} hubUrl
     * @return {Promise<unknown>}
     */
    async connectTo(hubUrl) {
        throw new Error("Abstract method should be overridden by the concrete connection")
    }


    /**
     * Requests a server-side disconnect, then marks the local actor as explicitly disconnected.
     *
     * The hub exposes disconnection over HTTP even when the active transport is WebSocket/SSE;
     * callers should use this instead of simply closing the client transport when the server must cleanup state.
     *
     * @return {Promise<void>}
     * @throws {Error} If the connection has not been established or if there is an error during disconnection.
     */
    async disconnect() {
        // Hub disconnect endpoint is HTTP(S) even for WS transports; normalize the scheme for the DELETE request.
        let url = this.hubUrl.replace("ws", "http");
        if (!this.connected) {
            throw new Error("Connection has not been established");
        }
        if (this._lastTicket !== "") {
            url += `?ticket=${this._lastTicket}`
        }
        let response;
        try {
            response = await fetch(url, {
                method: 'DELETE'
            });
            if (!response.ok) {
                const message = await response.text();
                throw new Error(`${message} (${response.status})`);
            }
        } catch (e) {
            Log.error(`[Actor.disconnect] Error during disconnection: ${e.message}`);
            throw new Error(`Disconnection failed: ${e.message}`);
        } finally {
            this.connected = false;
            this.explicitlyDisconnected = true;
        }
        return Promise.resolve();
    }

    /**
     * @param {number} [nb=0]
     * @param {number} [maxRetries=50]
     * @param {number} [timeout=5000]
     * @returns {Promise<void>}
     */
    async waitForConnections(nb = 0, maxRetries = 50, timeout = 5000) {
        return await this.hubInfo.waitForConnections(nb, maxRetries, timeout);
    }


    /**
     * Reserves a claimed name on the hub and returns a ticket required for the subsequent connect.
     * @param {string} clientName
     * @param {string} [activation=""]
     * @return {Reservation}
     */
    async claimName(clientName, activation = "") {
        try {
            const response = await SecurityAgent.fetch(this.UUID, this.hubInfo.aesKey, `${this.hubInfo.host}/api/v1/actors/client/claims/name/in/${this.hubInfo.name}`, {
                method: 'POST',
                headers: HubInfo.httpHeaders(),
                body: JSON.stringify({client: clientName, activation: activation})
            })
            return await response.json();
        } catch (e) {
            Log.error('[Actor.claimName] Error reserving client name:', e.message);
            throw e;
        }
    }


    /////////////////////
    // event API
    /////////////////////


    /**
     * Ergonomic alias kept for parity with common event-emitter APIs.
     * @param eventType {string}
     * @param callback {function(ActorEvent)}
     * @param options {AddEventListenerOptions}
     */
    on(eventType, callback, options = null) {
        this.addEventListener(eventType, callback, options);
    }


    /**
     * `once` waits for a single event and enforces a strict settlement contract:
     * - resolves with a non-error {@link ActorEvent}
     * - rejects with an error {@link ActorEvent}
     * - rejects with a native {@link Error} on timeout
     *
     * @param {string} eventType
     * @param {number} timeout
     * @param {AddEventListenerOptions|null} options
     * @returns {Promise<ActorEvent>}
     */
    once(eventType, timeout = TimeoutForOnce, options = null) {
        return new Promise((resolve, reject) => {
            let timeoutId = null;

            const listener = (event) => {
                // Remove before settling to prevent reentrancy/double-settlement if user code synchronously triggers the same event.
                try {
                    this.removeEventListener(eventType, listener, options);
                } catch (_) {
                    // best-effort
                }
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
                if (event && typeof event.isError === "function" && event.isError()) {
                    reject(event);
                } else {
                    resolve(event);
                }
            };

            if (timeout > 0) {
                timeoutId = setTimeout(() => {
                    try {
                        this.removeEventListener(eventType, listener, options);
                    } catch (_) {
                        // best-effort
                    }
                    reject(new Error(`Timeout for event ${eventType}`));
                }, timeout);
            }

            this.addEventListener(eventType, listener, options);
        });
    }


    /**
     * Creates an {@link ActorEvent} pre-wired to this actor with a fresh callId.
     * @param type {string}
     * @param {Object} options
     * @param instruction {string}
     * @param {Addresses} addresses
     * @returns {ActorEvent}
     */
    newEvent(type, options, instruction, addresses) {
        return new ActorEvent(type, options, this, this.hexIncrementalID(), instruction, addresses);
    }

    /**
     * Callback-based RPC helper.
     *
     * Delivery contract:
     * - `success(event)` is invoked on a remote answer (instruction `'a'`).
     * - `failure(event)` is invoked on:
     *    - a remote error reply (instruction `'e'`)
     *    - a timeout (synthetic error event, instruction `'e'`)
     *    - a local send failure (synthetic error event, instruction `'e'`)
     *
     * The returned Promise represents only the local send lifecycle (encryption + transport).
     *
     * @param eventType {string}
     * @param data {*}
     * @param {function(ActorEvent):void} success
     * @param {function(ActorEvent):void} failure
     * @param {Addresses} addresses
     * @param {number} [answerLimit=Once]
     * @param {number} [timeout=TimeoutForOnce]
     */
    call(eventType, data, success, failure, addresses = DefaultAddresses, answerLimit = Once, timeout = TimeoutForOnce) {
        if (!data) {
            data = {};
        }
        let e = this.newEvent(eventType, {detail: data}, 'c', addresses);
        return e.call(success, failure, addresses, answerLimit, timeout);
    }

    /**
     * Promise-based RPC helper.
     *
     * Contract:
     * - Resolves with the answer {@link ActorEvent} (instruction 'a').
     * - Rejects ONLY with {@link ActorError}, regardless of whether the failure is remote, timeout, or local transport.
     *
     * @param {string} eventType
     * @param {*} data
     * @param {Addresses} addresses
     * @param {number} [answerLimit=Once]
     * @param {number} [timeout=TimeoutForOnce]
     * @returns {Promise<ActorEvent>}
     * @throws {ActorError}
     */
    callPromise(eventType, data, addresses = DefaultAddresses, answerLimit = Once, timeout = TimeoutForOnce) {
        return new Promise((resolve, reject) => {
            let settled = false;

            const safeResolve = (v) => {
                if (settled) {
                    return;
                }
                settled = true;
                resolve(v);
            };

            const safeReject = (e) => {
                if (settled) {
                    return;
                }
                settled = true;
                reject(e);
            };

            // Preserve call context so errors remain actionable even when the transport fails before a reply exists.
            let callId = null;
            let callName = (typeof eventType === "string" && eventType !== "") ? eventType : null;

            // Mirror Actor.call(...) behavior: default falsy data to {}.
            if (!data) {
                data = {};
            }

            /** @type {ActorEvent|undefined} */
            let callEvent;

            try {
                callEvent = this.newEvent(eventType, {detail: data}, CallInstruction, addresses);
                callId = callEvent.callId;
                callName = callEvent.type;
            } catch (e) {
                safeReject(ActorError.fromTransportError(e, {
                    stage: "internal",
                    kind: "internal",
                    callId: callId,
                    callName: callName,
                    addresses: addresses
                }));
                return;
            }

            try {
                const p = callEvent.call(
                    (event) => {
                        safeResolve(event);
                    },

                    (event) => {
                        try {
                            safeReject(ActorError.fromActorEventError(event));
                        } catch (e) {
                            // Guardrail: callers should never have to handle non-ActorError rejections from callPromise.
                            safeReject(ActorError.fromTransportError(e, {
                                stage: "internal",
                                kind: "internal",
                                callId: callId,
                                callName: callName,
                                addresses: addresses
                            }));
                        }
                    },

                    addresses,
                    answerLimit,
                    timeout
                );

                // Ensure local send failures cannot become unhandled rejections (caller already relies on ActorError-only rejection).
                if (p && typeof p.catch === "function") {
                    p.catch((e) => {
                        if (settled) {
                            return;
                        }
                        safeReject(ActorError.fromTransportError(e, {
                            stage: "send",
                            callId: callId,
                            callName: callName,
                            addresses: addresses
                        }));
                    });
                }
            } catch (e) {
                safeReject(ActorError.fromTransportError(e, {
                    stage: "internal",
                    kind: "internal",
                    callId: callId,
                    callName: callName,
                    addresses: addresses
                }));
            }
        });
    }


    /**
     * Fire-and-forget send path: avoids emitter allocation when no reply is expected.
     * @param eventType {string}
     * @param data {*}
     * @param addresses {Addresses}
     * @returns {Promise<void>}
     */
    triggerEvent(eventType, data = {}, addresses = DefaultAddresses) {
        Log.dev('[Actor.triggerEvent] Triggering event:', eventType);
        return this.sendCallMessage(this.hexIncrementalID(), eventType, data, addresses);
    }

    /**
     * Protocol ingress for both WebSocket and SSE transports.
     *
     * This keeps protocol parsing/decryption in one place so transport subclasses only deal with I/O.
     * Fragmented messages are reassembled before dispatch.
     *
     * @param cipheredMessage
     */
    async handleIncomingMessage(cipheredMessage) {
        let message;
        try {
            message = await SecurityAgent.decrypt(this.hubInfo.aesKey, cipheredMessage);
        } catch (e) {
            throw new Error(`[Actor.handleIncomingMessage] Error decrypting message: ${e.message}`);
        }
        if (message.length > 1) {
            if (message.length > 5) {
                // SSE delivers frames as "data: <message>\n\n"; normalize to the raw protocol message.
                if (message.startsWith("data:")) {
                    message = message.substring(6);
                }
            }

            // Envelope is `sender:<body>:recipient`; payload may contain ':' so only the first/last segments are addresses.
            const parts = message.split(':');
            const sender = parts.shift();
            const recipient = parts.pop();
            let body = parts.join(':');

            // Body is `instruction callId callName payload`; payload may contain whitespace so capture the remainder as-is.
            let match = body.match(/^(\S*)\s(\S*)\s(\S*)\s([\s\S]*)$/);
            let instruction, callId, callName, payload;
            if (match) {
                instruction = match[1];
                callId = match[2];
                callName = match[3];
                payload = match[4];
            } else {
                throw new Error(`[Actor.handleIncomingMessage] Error parsing message: \"${message}\"`);
            }
            if (Log.supports(Log.Dev)) {
                Log.dev(`[Actor.handleIncomingMessage] Received sender='${sender}' inst='${instruction}' callName='${callName}' callId='${callId}' payLoad='${payload}' recipient='${recipient}'`);
            }

            const execMessage = new ExecutionMessage(sender, recipient, instruction, callId, callName, payload);

            if (callName.startsWith(Fragment)) {
                // Fragmentation metadata is encoded in callName to stay compatible with the server-side protocol framing.
                let assembler = this.assemblers.get(callId);
                if (!assembler) {
                    try {
                        assembler = new Assembler(execMessage, (finalMessage) => {
                            this._reactToExecutionMessage(finalMessage)
                        });
                    } catch (e) {
                        // best-effort: ignore malformed/out-of-order fragment streams; the sender may restart with a new callId.
                    }
                    this.assemblers.set(callId, assembler);
                } else {
                    // The peer may cancel an in-flight assembly; stop buffering to avoid leaks.
                    const resume = assembler.addFragmentedMessage(execMessage);
                    if (!resume) {
                        if (Log.supports(Log.Dev)) {
                            Log.dev("[Actor.handleIncomingMessage] fragmented message has been interrupted by the peer");
                        }
                        this.assemblers.delete(callId);
                    }
                }
            } else {
                this._reactToExecutionMessage(execMessage);
            }
        } else {
            if (Log.supports(Log.Dev)) {
                Log.dev('[Actor.handleIncomingMessage] Received short message:', message);
            }
        }
    }


    /**
     * Dispatches a fully-parsed execution message (assembled or not) into the local listener/emitter pipeline.
     * @param {ExecutionMessage} message
     * @private
     */
    _reactToExecutionMessage(message) {
        let data;
        try {
            data = JSON.parse(message.payload);
        } catch (e) {
            // Preserve payload fidelity: non-JSON payloads still need to surface verbatim for debugging and remote error propagation.
            Log.error('[Actor.handleIncomingMessage] Call Error parsing JSON:', message.payload, "error", e.message);
            data = message.payload;
        }
        let event = new ActorEvent(message.callName, {detail: data}, this, message.callId, message.instruction, new Addresses(message.sender, message.recipient));
        if (message.instruction === CallInstruction) {
            try {
                this.dispatchEvent(event);
            } catch (e) {
                throw new Error(`[Actor.handleMessage] Error dispatching call event: ${e.message}`);
            }
        } else if (message.instruction === AnswerInstruction || message.instruction === ErrorInstruction) {
            const emitter = this.emitters.get(message.callId);
            if (emitter) {
                if (emitter.callName !== message.callName) {
                    // Reply routing is callId + callName; mismatch indicates protocol desync and must not be silently accepted.
                    throw new Error(`[Actor.handleMessage] Answer Error: callName does not match: ${message.callName} ${emitter.callName}`);
                } else {
                    emitter.handleReply(event);
                }
            } else {
                // Hubs may broadcast replies/errors; if no local call is awaiting this callId, treat it as a normal inbound event.
                try {
                    this.dispatchEvent(event);
                } catch (e) {
                    throw new Error(`[Actor.handleMessage] Error dispatching Answer event: ${e.message}`);
                }
            }
        } else {
            Log.dev('[Actor.handleIncomingMessage] Received unknown message:', message);
        }
    }

    /**
     * @param callId
     * @param callName
     * @param params
     * @param addresses
     * @returns {Promise<void>}
     */
    sendCallMessage(callId, callName, params, addresses = DefaultAddresses) {
        const execMessage = new ExecutionMessage(addresses.sender.value, addresses.receiver.value, CallInstruction, callId, callName, JSON.stringify(params));
        return this.sendExecutionMessage(execMessage);
    }

    /**
     * @param callId
     * @param callName
     * @param data
     * @param addresses {Addresses}
     * @returns {Promise<void>}
     */
    sendAnswerMessage(callId, callName, data, addresses = DefaultAddresses) {
        const execMessage = new ExecutionMessage(addresses.sender.value, addresses.receiver.value, AnswerInstruction, callId, callName, JSON.stringify(data));
        return this.sendExecutionMessage(execMessage);
    }

    /**
     * @param callId
     * @param callName
     * @param error
     * @param addresses {Addresses}
     * @returns {Promise<void>}
     */
    sendErrorMessage(callId, callName, error, addresses = DefaultAddresses) {
        const execMessage = new ExecutionMessage(addresses.sender.value, addresses.receiver.value, ErrorInstruction, callId, callName, JSON.stringify(error));
        return this.sendExecutionMessage(execMessage);
    }


    /**
     * Sends an execution message using the concrete transport.
     *
     * Transport implementations must reject on send failures so higher layers can reliably surface local-send errors
     * (notably for {@link Actor#callPromise}).
     *
     * @param {ExecutionMessage} message
     * @param {string} [encoding=NoEncoding]
     * @param {number} [timeOut=5000]
     * @returns {Promise<void>}
     */
    sendExecutionMessage(message, encoding = NoEncoding, timeOut = 5000) {
        throw new Error("abstract method needs to be overridden by for a given transport");
    }


    ////////////////////
    // EventTarget API
    ////////////////////


    /**
     * Local listener registry (does not rely on native EventTarget internals to avoid runtime-specific behavior differences).
     * @param type {string}
     * @param callback {function(ActorEvent)}
     * @param options {AddEventListenerOptions}
     */
    addEventListener(type, callback, options) {
        if (!this.listeners.has(type)) {
            this.listeners.set(type, []);
        }
        const listener = new ActorEventListener(this, type, callback, options);
        Log.dev('[Actor.addEventListener] Adding listener:', type);
        this.listeners.get(type).push(listener);
    }

    /**
     * @param type {string}
     * @param callback {function(ActorEvent)}
     * @param options {AddEventListenerOptions}
     */
    removeEventListener(type, callback, options) {
        if (!this.listeners.has(type)) {
            return;
        }
        Log.dev('[Actor.removeEventListener] Removing listener:', type);
        const listeners = this.listeners.get(type);
        const listenerIndex = listeners.findIndex(listener => listener.callback === callback);
        if (listenerIndex >= 0) {
            listeners.splice(listenerIndex, 1);
        }
    }

    /**
     * Actor only dispatches protocol-level {@link ActorEvent} instances.
     * This intentionally avoids DOM-style bubbling/capture semantics and keeps delivery deterministic for RPC usage.
     * @param event {ActorEvent|Event}
     */
    dispatchEvent(event) {
        if (event instanceof ActorEvent) {
            const type = event.type;
            if (!this.listeners.has(type)) {
                Log.info('[Actor.dispatchEvent] No listener for event:', type);
            } else {
                const listeners = this.listeners.get(type);
                Log.dev('[Actor.dispatchEvent] Dispatching event to listeners:', type, "listeners.length", listeners.length);
                listeners.forEach(listener => {
                    listener.callback(event);
                });
            }
        } else {
            throw new Error("Only ActorEvent instances should be dispatched by an Actor instance.");
        }
    }


    /////////////////////
    // Emitters APi
    /////////////////////

    /**
     * Registers an emitter for an in-flight call so replies can be routed back to the call site.
     * @param emitter {EmitterReplyListener}
     */
    addEmitter(emitter) {
        Log.dev('[Actor.addEmitter] Adding EmitterReplyListener:', emitter.callId, emitter.callName);
        if (this.emitters.has(emitter.callId)) {
            throw new Error(`Emitter already exists: ${emitter.callId} ${emitter.callName}`);
        }
        this.emitters.set(emitter.callId, emitter);
    }


    /**
     * Clears the local emitter and its timer to avoid reply-routing leaks.
     * @param emitter {EmitterReplyListener}
     */
    removeEmitter(emitter) {
        Log.dev('[Actor.removeEmitterListener] Removing emitterListener:', emitter.callId, emitter.callName);
        const e = this.emitters.get(emitter.callId);
        if (!e) {
            return;
        }
        const timeoutId = e.timeoutId;
        if (timeoutId !== null) {
            clearTimeout(timeoutId);
        }
        this.emitters.delete(emitter.callId);
    }
}

export {Actor};